import React, { useEffect, useState } from "react";
import Icon from "@mdi/react";
import { mdiCalendar, mdiChevronDown, mdiChevronLeft, mdiChevronRight } from "@mdi/js";
import { Dropdown as BootStrapDropdown, DropdownToggle, DropdownMenu } from "reactstrap";
import moment, { Moment } from "moment-timezone";

import { useStartDate } from "../../services/DateService";
import styles from "./styles.scss";

export interface DatePickerProps {
  style?: object;
  className?: string;
  onSelectDate(selectedDate: Moment): void;
  selectedDate: Moment;
}

export interface DatePickerState {
  currentMonth: Moment;
  openDropdown: boolean;
}

/**
 * A date picker component that allows the user to select a single day from a calendar view
 */
const DatePicker = (props: DatePickerProps) => {
  const [currentMonth, setCurrentMonth] = useState(props.selectedDate);
  const [openDropdown, setOpenDropdown] = useState(false);

  useEffect(() => {
    setCurrentMonth(moment.tz(Intl.DateTimeFormat().resolvedOptions().timeZone));
  }, []);

  /**
   * This helper function opens and closes the dropdown
   */
  const toggleDropdown = () => setOpenDropdown(!openDropdown);

  /**
   * This helper function increments the currentMonth forward by one month
   */
  const nextMonth = () => setCurrentMonth(currentMonth.clone().add(1, "M"));

  /**
   * This helper function decrements the currentMonth forward by one month
   */
  const prevMonth = () => setCurrentMonth(currentMonth.clone().subtract(1, "M"));

  /**
   * This function will render the prev and next month buttons as well as
   * display the current month and year
   */
  const renderHeader = () => {
    return (
      <div className="rotoql-date-picker__header">
        <button className="rotoql-date-picker__header-left-button" onClick={prevMonth}>
          <Icon path={mdiChevronLeft} size="26px" color="#262626" />
        </button>
        <div className="rotoql-date-picker__header-selected-month">{currentMonth.format("MMMM YYYY")}</div>
        <button className="rotoql-date-picker__header-right-button" onClick={nextMonth}>
          <Icon path={mdiChevronRight} size="26px" color="#262626" />
        </button>
      </div>
    );
  };

  /**
   * This function will render the day name headers
   */
  const renderDays = () => {
    const days: JSX.Element[] = [];
    let startDate: Moment = currentMonth.clone().startOf("week");
    for (let i = 0; i < 7; i++) {
      days.push(
        <div className="rotoql-date-picker__day" key={i}>
          {startDate.clone().add(i, "d").format("dd").charAt(0)}
        </div>
      );
    }
    return <div className="rotoql-date-picker__day-row">{days}</div>;
  };

  /**
   * This function will ensure that a disabled element cannot be clicked before it
   * tries to return the date the user clicked on.
   */
  const onSelectDate = (selectedDate: Moment, disabled: boolean) => {
    if (!disabled) {
      props.onSelectDate(selectedDate);
      toggleDropdown();
    }
  };

  /**
   * This is the meat of the component. It will render out the days of the month
   * highlighting the currently selected date if its within the range and fading
   * out any date that isn't in the currently selected month.
   */
  const renderCells = () => {
    const monthStart: Moment = currentMonth.clone().startOf("month");
    const monthEnd: Moment = monthStart.clone().endOf("month");
    const startDate: Moment = monthStart.clone().startOf("week");
    const endDate: Moment = monthEnd.clone().endOf("week");

    const rows: JSX.Element[] = [];
    let days: JSX.Element[] = [];
    let day: Moment = startDate;
    let formattedDate: string = "";
    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = day.format("D");
        const cloneDay: Moment = day.clone();
        const disabled: boolean = !day.isSame(monthStart, "month");
        days.push(
          <div
            className={`rotoql-date-picker__calendar-cell ${
              disabled
                ? "rotoql-date-picker__calendar-cell--disabled"
                : day.isSame(props.selectedDate, "day")
                ? "rotoql-date-picker__calendar-cell--selected"
                : day.isSame(moment(), "day")
                ? "rotoql-date-picker__calendar-cell--today"
                : ""
            }`}
            key={day.format()}
            onClick={() => onSelectDate(cloneDay, disabled)}
          >
            <span>{formattedDate}</span>
          </div>
        );
        day = day.add(1, "d");
      }
      rows.push(
        <div className="rotoql-date-picker__calendar-row" key={day.format()}>
          {days}
        </div>
      );
      days = [];
    }
    return <div className="rotoql-date-picker__calendar-body">{rows}</div>;
  };

  const startDate = useStartDate(props.selectedDate);

  return (
    <BootStrapDropdown
      isOpen={openDropdown}
      toggle={toggleDropdown}
      className={`rotoql-date-picker ${props.className}`}
    >
      <DropdownToggle block style={props.style} className="rotoql-date-picker__button">
        <div className="rotoql-date-picker__selected-text">
          <Icon path={mdiCalendar} size="20px" color="#00214D" />
          {startDate}
        </div>
        <Icon path={mdiChevronDown} size="26px" color="#00214D" />
      </DropdownToggle>
      <DropdownMenu className="rotoql-date-picker__menu">
        {renderHeader()}
        {renderDays()}
        {renderCells()}
      </DropdownMenu>
      <style jsx global>
        {styles}
      </style>
    </BootStrapDropdown>
  );
};

DatePicker.defaultProps = {
  onSelectDate: () => {},
  selectedDate: moment(),
};

export default DatePicker;
